import { type Handle, css } from 'remix/ui'
import { readAppSession } from '#client/app-session-context.tsx'
import { reveal } from '#client/reveal.ts'
import { type RouteLoaderResult } from '#client/route-loader.ts'
import {
	computeOverageRatesUsd,
	formatDurableObjectRowsRead,
	formatMinJobInterval,
	planLimits,
	weeklyComputeWindowNote,
	type PlanLimits,
} from '#universal/plans.ts'
import {
	publicSignupPrimaryCta,
	type PublicSignupCta,
} from '#universal/public-signup-copy.ts'
import { docHref } from '#universal/docs-nav.ts'
import { colors, radius, typography } from '#universal/styles/tokens.ts'
import {
	getGhostButtonCss,
	getPillButtonCss,
	getSurfaceCardCss,
	layoutMaxWidths,
	pageHeadCss,
	visuallyHiddenCss,
} from '#universal/styles/style-primitives.ts'

/**
 * Pricing page, ported from the redesign prototype (`landing/pricing.html`).
 * Two flat plan panels over a 44rem spec-sheet measure; Pro's green border is
 * the only loud element. Limits render as one honest table grouped under
 * display-face section titles — every value comes from `plans.ts`, never
 * hardcoded here.
 */

type LimitValue = {
	text: string
	muted?: boolean
}

type LimitRow = {
	label: string
	key: keyof PlanLimits
	format?: (value: number) => LimitValue
}

type LimitGroup = {
	title: string
	note?: string
	rows: ReadonlyArray<LimitRow>
}

const count = new Intl.NumberFormat('en-US')
const size = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 })
const factoryGuideHref = docHref('kody-factory')

const limitGroups: ReadonlyArray<LimitGroup> = [
	{
		title: 'Packages & jobs',
		rows: [
			{ label: 'Connected repos', key: 'maxRepos' },
			{ label: 'Saved packages', key: 'maxSavedPackages' },
			{ label: 'Scheduled jobs', key: 'maxScheduledJobs' },
			{
				label: 'Fastest job interval',
				key: 'minJobIntervalMs',
				format: (value) => ({ text: formatMinJobInterval(value) }),
			},
			{ label: 'Active repo sessions', key: 'maxRepoSessions' },
		],
	},
	{
		title: 'Compute',
		note: weeklyComputeWindowNote,
		rows: [
			{ label: 'Concurrent workflows', key: 'maxConcurrentWorkflows' },
			{ label: 'Execute calls per day', key: 'maxExecuteCallsPerDay' },
			{ label: 'Execute calls per week', key: 'maxExecuteCallsPerWeek' },
			{ label: 'Outbound fetches per day', key: 'maxOutboundFetchesPerDay' },
			{ label: 'Outbound fetches per week', key: 'maxOutboundFetchesPerWeek' },
			{ label: 'Job runs per day', key: 'maxJobRunsPerDay' },
			{
				label: 'Unique worker days per month',
				key: 'maxUniqueWorkerDaysPerMonth',
			},
			{
				label: 'Durable Object rows read per month',
				key: 'maxDurableObjectRowsReadPerMonth',
				format: (value) => ({ text: formatDurableObjectRowsRead(value) }),
			},
		],
	},
	{
		title: 'Email',
		rows: [
			{ label: 'Sends per day', key: 'maxEmailSendsPerDay' },
			{ label: 'Receives per day', key: 'maxEmailReceivesPerDay' },
			{ label: 'Stored messages', key: 'maxStoredEmailMessages' },
			{
				label: 'Maximum message size',
				key: 'maxEmailMessageBytes',
				format: (value) => ({ text: formatLimitBytes(value) }),
			},
		],
	},
	{
		title: 'Storage & secrets',
		rows: [
			{ label: 'Stored secrets', key: 'maxSecrets' },
			{
				label: 'Durable storage',
				key: 'maxStorageBytes',
				format: (value) => ({ text: formatLimitBytes(value) }),
			},
		],
	},
]

export async function pricingRouteLoader(): Promise<RouteLoaderResult> {
	return {}
}

export function PricingRoute(handle: Handle) {
	return () => {
		const isSignedIn = readAppSession(handle).session !== null
		const signedOutCta = publicSignupPrimaryCta()
		return (
			<section mix={css(pricingCss)}>
				<header mix={css(pageHeadCss)}>
					<h1 data-rise style={{ '--rise': '0' }}>
						Start free.
						<br />
						Pay when Kody <em>earns it</em>.
					</h1>
					<p>
						The home your agents share is a factory: it turns the work they do
						once into durable software that keeps running. Every plan is the
						whole factory. You pay for volume.
					</p>
				</header>

				<div mix={css(plansCss)}>
					<section
						aria-labelledby="plan-free"
						mix={[css(planPanelCss), reveal()]}
					>
						<h2 id="plan-free" mix={css(planTitleCss)}>
							Free
						</h2>
						<p mix={css(planPriceCss)}>$0</p>
						<p mix={css(planCopyCss)}>
							The whole {factoryGuideLink()}. 5 jobs, no faster than every 15
							minutes.
						</p>
						{isSignedIn ? (
							<a href="/account" mix={css(planPillButtonCss)}>
								Open your account
							</a>
						) : (
							<a href={signedOutCta.href} mix={css(planPillButtonCss)}>
								{signedOutCta.label}
							</a>
						)}
					</section>

					{/*
					 * Standard carries the accent. The prototype accented the one
					 * paid plan, and Standard is that plan — Pro is the newer
					 * tier above it. Accenting both would make the border stop
					 * meaning anything.
					 */}
					<section
						aria-labelledby="plan-standard"
						mix={[css(featuredPlanPanelCss), reveal(90)]}
					>
						<h2 id="plan-standard" mix={css(planTitleCss)}>
							Standard
						</h2>
						<p mix={css(featuredPlanPriceCss)}>
							$12<small mix={css(planPriceUnitCss)}>/month</small>
						</p>
						<p mix={css(planPriceNoteCss)}>$10/mo billed annually</p>
						<p mix={css(planCopyCss)}>
							Same {factoryGuideLink()}. More room for jobs, workflows, and
							daily volume.
						</p>
						{renderPaidPlanCta(isSignedIn, signedOutCta)}
					</section>

					<section
						aria-labelledby="plan-pro"
						mix={[css(planPanelCss), reveal(180)]}
					>
						<h2 id="plan-pro" mix={css(planTitleCss)}>
							Pro
						</h2>
						<p mix={css(planPriceCss)}>
							$49<small mix={css(planPriceUnitCss)}>/month</small>
						</p>
						<p mix={css(planPriceNoteCss)}>$40/mo billed annually</p>
						<p mix={css(planCopyCss)}>
							Same {factoryGuideLink()}. More room for storage, jobs, workflows,
							and daily volume.
						</p>
						{renderPaidPlanCta(isSignedIn, signedOutCta)}
					</section>

					{/*
					 * Contact strip, not a fourth SKU. No price, no Max
					 * column, no feature-matrix cells — Teams/Enterprise and
					 * Max stay manual.
					 */}
					<section
						aria-labelledby="plan-teams"
						mix={[css(teamsInviteCss), reveal(240)]}
					>
						<div>
							<h2 id="plan-teams" mix={css(planTitleCss)}>
								Teams / Enterprise
							</h2>
							<p mix={css(teamsInviteCopyCss)}>
								Running Kody across a team or with higher needs? Email us —
								we&rsquo;re shaping that offering and want to hear your needs.
							</p>
						</div>
						<a href="mailto:kody@kody.codes" mix={css(teamsInviteButtonCss)}>
							Email us
						</a>
					</section>
				</div>

				<section aria-labelledby="limits-title" mix={css(limitsCss)}>
					<h2 id="limits-title" mix={css(limitsTitleCss)}>
						Here&rsquo;s what you get
					</h2>
					<div mix={css(limitsScrollCss)}>
						<table mix={css(limitsTableCss)}>
							<thead>
								<tr>
									<th scope="col">
										<span mix={css(visuallyHiddenCss)}>Resource</span>
									</th>
									<th scope="col">
										<span data-limits-plan>Free</span>
										<span data-limits-price>$0</span>
									</th>
									<th scope="col">
										<span data-limits-plan>Standard</span>
										<span data-limits-price>$12/mo</span>
										<span data-limits-annual>$10/mo billed annually</span>
									</th>
									<th scope="col">
										<span data-limits-plan>Pro</span>
										<span data-limits-price>$49/mo</span>
										<span data-limits-annual>$40/mo billed annually</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{limitGroups.flatMap((group) => [
									<tr key={group.title} data-group>
										<th colspan={4}>{group.title}</th>
									</tr>,
									...group.rows.map((row) => (
										<tr key={row.key}>
											<th scope="row">{row.label}</th>
											{renderLimitCell(row, planLimits.free)}
											{renderLimitCell(row, planLimits.standard)}
											{renderLimitCell(row, planLimits.pro)}
										</tr>
									)),
									...(group.note
										? [
												<tr key={`${group.title}-note`} data-note>
													<td colspan={4}>{group.note}</td>
												</tr>,
											]
										: []),
								])}
							</tbody>
						</table>
					</div>
					<p mix={css(limitsFootnoteCss)}>
						Overage is billed monthly at $
						{computeOverageRatesUsd.uniqueWorkerDay} per unique worker day and $
						{computeOverageRatesUsd.durableObjectRowsReadPerMillion} per million
						Durable Object rows read above the included allotment, for
						public-ladder accounts with a payment method on file. Free accounts
						that exceed an include and have no payment method are asked to
						upgrade instead of being charged. Free accounts that already have a
						Stripe customer are invoiced like other public-ladder accounts.
						Those allotments are not hard-cut. Grandfathered legacy Standard/Pro
						accounts are not billed for these meters until they leave the legacy
						ladder. Execute and outbound fetches are hard daily and weekly caps
						on public plans (whichever window hits first blocks). Grandfathered
						legacy Standard/Pro and Max stay daily-only. Durable Object duration
						is unmetered.
					</p>
				</section>

				<p mix={css(limitsCtaCss)}>
					{isSignedIn ? (
						<a href="/account" mix={css(limitsCtaButtonCss)}>
							Open your account
						</a>
					) : (
						<a href={signedOutCta.href} mix={css(limitsCtaButtonCss)}>
							{signedOutCta.label}
						</a>
					)}
				</p>
			</section>
		)
	}
}

function factoryGuideLink() {
	return <a href={factoryGuideHref}>factory</a>
}

function renderPaidPlanCta(isSignedIn: boolean, signedOutCta: PublicSignupCta) {
	if (isSignedIn) {
		return (
			<a href="/account/billing" mix={css(planGhostButtonCss)}>
				Upgrade in billing
			</a>
		)
	}
	return (
		<a href={signedOutCta.href} mix={css(planGhostButtonCss)}>
			{signedOutCta.label}
		</a>
	)
}

function renderLimitCell(row: LimitRow, limits: PlanLimits) {
	const value = limits[row.key]
	if (value == null) {
		return <td>—</td>
	}
	const cell = row.format ? row.format(value) : { text: count.format(value) }
	return (
		<td>
			{cell.muted ? (
				<span mix={css({ color: colors.textMuted })}>{cell.text}</span>
			) : (
				cell.text
			)}
		</td>
	)
}

function formatLimitBytes(value: number): string {
	const kibibyte = 1024
	const mebibyte = 1024 * kibibyte
	const gibibyte = 1024 * mebibyte
	// Bounded fraction digits: a limit that is not a clean power of two would
	// otherwise render with `Intl`'s default three decimals (750000 bytes as
	// "732.422 KiB"), which reads like precision the table does not mean.
	if (value >= gibibyte) return `${size.format(value / gibibyte)}\u00A0GiB`
	if (value >= mebibyte) return `${size.format(value / mebibyte)}\u00A0MiB`
	return `${size.format(value / kibibyte)}\u00A0KiB`
}

const pricingCss = {
	maxWidth: layoutMaxWidths.extended,
	marginInline: 'auto',
	padding:
		'clamp(3rem, 7vw, 5.5rem) clamp(1.25rem, 4vw, 2.5rem) clamp(4rem, 8vw, 6.5rem)',
}

/*
 * Three tiers on the prototype's two-up measure would squeeze each card under
 * the ~17rem its price and copy need, so the measure widens with the extra
 * column and drops straight to one column rather than leaving an orphan.
 */
const plansCss = {
	width: 'min(100%, 62rem)',
	margin: 'clamp(2.5rem, 6vw, 4rem) auto 0',
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	gap: '1.2rem',
	'@media (max-width: 860px)': {
		width: 'min(100%, 44rem)',
		gridTemplateColumns: '1fr',
	},
}

const planPanelCss = {
	...getSurfaceCardCss(),
	display: 'flex',
	flexDirection: 'column' as const,
	alignItems: 'flex-start',
	gap: '1rem',
	textAlign: 'left' as const,
	/* Prototype `.plan` sits one step above the base card radius. */
	borderRadius: `calc(${radius.card} + 4px)`,
	padding: 'clamp(1.5rem, 3vw, 2rem)',
	'@media (max-width: 680px)': {
		alignItems: 'stretch',
		textAlign: 'center' as const,
	},
}

/*
 * The featured plan carries the accent; the border is the only loud thing on
 * the page.
 */
const featuredPlanPanelCss = {
	...planPanelCss,
	borderColor: `oklch(from ${colors.primary} l c h / 0.6)`,
}

const planTitleCss = {
	margin: 0,
	fontSize: '1.15rem',
	fontWeight: 720,
	letterSpacing: '-0.012em',
}

const planPriceCss = {
	margin: 0,
	fontFamily: typography.fontFamilyDisplay,
	fontWeight: 760,
	fontSize: 'clamp(2.1rem, 4vw, 2.6rem)',
	lineHeight: 1,
	letterSpacing: '-0.025em',
}

const featuredPlanPriceCss = {
	...planPriceCss,
	color: colors.primaryText,
}

const planPriceUnitCss = {
	font: `550 1rem/1 ${typography.fontFamilyBody}`,
	color: colors.textMuted,
	letterSpacing: 0,
	marginLeft: '0.2rem',
}

const planPriceNoteCss = {
	margin: 0,
	color: colors.textMuted,
	fontSize: '0.92rem',
}

const planCopyCss = {
	margin: 0,
	color: colors.textMuted,
	fontSize: '0.98rem',
	maxWidth: '34ch',
	textWrap: 'pretty' as const,
	'& a': {
		color: colors.primaryText,
		textDecoration: 'underline',
		textUnderlineOffset: '0.15em',
	},
	'@media (max-width: 680px)': {
		marginInline: 'auto',
	},
}

const planButtonSizeCss = {
	marginTop: 'auto',
	fontSize: '0.95rem',
	padding: '0.8rem 1.35rem',
	maxWidth: '100%',
	boxSizing: 'border-box' as const,
	'@media (max-width: 680px)': {
		marginTop: '0.4rem',
	},
}

const planPillButtonCss = {
	...getPillButtonCss(),
	...planButtonSizeCss,
}

const planGhostButtonCss = {
	...getGhostButtonCss(),
	...planButtonSizeCss,
}

const teamsInviteCss = {
	...getSurfaceCardCss(),
	gridColumn: '1 / -1',
	display: 'flex',
	flexDirection: 'row' as const,
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: '1.2rem',
	textAlign: 'left' as const,
	borderRadius: `calc(${radius.card} + 4px)`,
	padding: 'clamp(1.5rem, 3vw, 2rem)',
	'@media (max-width: 680px)': {
		flexDirection: 'column' as const,
		alignItems: 'stretch',
		textAlign: 'center' as const,
	},
}

const teamsInviteCopyCss = {
	...planCopyCss,
	marginTop: '0.55rem',
	maxWidth: '62ch',
}

const teamsInviteButtonCss = {
	...getGhostButtonCss(),
	...planButtonSizeCss,
	marginTop: 0,
	flexShrink: 0,
	'@media (max-width: 680px)': {
		marginTop: '0.4rem',
	},
}

/* One honest table, no marketing checkmarks. Spec-sheet measure — narrow
   enough that the eye never loses the row between label and value. */
const limitsCss = {
	width: 'min(100%, 44rem)',
	margin: 'clamp(3.5rem, 8vw, 5.5rem) auto 0',
}

const limitsTitleCss = {
	margin: 0,
	fontSize: 'clamp(1.5rem, 2.6vw, 1.9rem)',
	fontWeight: 720,
	letterSpacing: '-0.018em',
	textAlign: 'center' as const,
}

const limitsScrollCss = {
	marginTop: '1.8rem',
	overflowX: 'auto' as const,
}

const limitsCtaCss = {
	margin: '2.4rem 0 0',
	textAlign: 'center' as const,
}

const limitsFootnoteCss = {
	margin: '1rem 0 0',
	color: colors.textMuted,
	font: `450 0.88rem/1.45 ${typography.fontFamilyBody}`,
}

const limitsCtaButtonCss = getPillButtonCss()

const limitsTableCss = {
	width: '100%',
	borderCollapse: 'collapse' as const,
	fontSize: '0.98rem',
	'& th, & td': {
		padding: '0.7rem 0.9rem',
		borderBottom: `1px solid ${colors.border}`,
	},
	/* Labels and values sit flush with the hairline edges; the three plan
	   columns are triplets so the tiers compare down a steady axis. */
	'& th:first-child, & td:first-child': {
		paddingLeft: '0.2rem',
	},
	'& th:last-child, & td:last-child': {
		paddingRight: '0.2rem',
	},
	'& thead th:not(:first-child), & td': {
		width: '7.5rem',
	},
	'& thead th': {
		font: `600 0.82rem/1.2 ${typography.fontFamilyBody}`,
		letterSpacing: '0.07em',
		textTransform: 'uppercase' as const,
		color: colors.textMuted,
		textAlign: 'left' as const,
		verticalAlign: 'bottom',
		borderBottomColor: colors.textMuted,
	},
	/* Plan header carries its price, so the table answers "which $?" alone. */
	'& [data-limits-plan]': {
		display: 'block',
		color: colors.text,
	},
	/*
	 * Third column is Standard, the featured plan — the accent has to follow
	 * the panel above it rather than sit on the last column (Pro).
	 */
	'& thead th:nth-child(3) [data-limits-plan]': {
		color: colors.primaryText,
	},
	'& [data-limits-price]': {
		display: 'block',
		marginTop: '0.1rem',
		font: `500 0.8rem/1.2 ${typography.fontFamilyBody}`,
		letterSpacing: 0,
		textTransform: 'none' as const,
		color: colors.textMuted,
	},
	'& [data-limits-annual]': {
		display: 'block',
		marginTop: '0.15rem',
		font: `500 0.72rem/1.25 ${typography.fontFamilyBody}`,
		letterSpacing: 0,
		textTransform: 'none' as const,
		color: colors.textMuted,
		whiteSpace: 'normal' as const,
	},
	/* Group titles are the table's spine: display face, extra air above. */
	'& tr[data-group] th': {
		padding: '2rem 0.2rem 0.55rem',
		font: `700 1.05rem/1.2 ${typography.fontFamilyDisplay}`,
		letterSpacing: '-0.01em',
		textTransform: 'none' as const,
		color: colors.text,
	},
	'& tbody tr:first-child th': {
		paddingTop: '1.1rem',
	},
	'& tbody th': {
		fontWeight: 450,
		color: colors.text,
		textAlign: 'left' as const,
	},
	'& tr[data-note] td': {
		padding: '0.45rem 0.2rem 0.85rem',
		color: colors.textMuted,
		fontSize: '0.88rem',
		whiteSpace: 'normal' as const,
		borderBottom: `1px solid ${colors.border}`,
	},
	'& td': {
		textAlign: 'left' as const,
		whiteSpace: 'nowrap' as const,
		fontVariantNumeric: 'tabular-nums',
		color: colors.text,
	},
}
